import React from "react";
import type { RuleParamValues } from "@/api";
import { roleAtLeast } from "@/lib/roles";
import { useLang, useT } from "../i18n";
import { formatRuleMoney } from "./rule-parameter-display";
import styles from "./screen-rule-reference.module.css";

export const RULE_REFERENCE_SNAPSHOT_VERSION = "2026-08-26.1";

export type RuleReferenceVersionStatus = "current" | "stale" | "unavailable";

export function ruleReferenceVersionStatus(
  runtimeRulesetVersion: string | null,
): RuleReferenceVersionStatus {
  if (!runtimeRulesetVersion) return "unavailable";
  return runtimeRulesetVersion === RULE_REFERENCE_SNAPSHOT_VERSION ? "current" : "stale";
}

export function canAccessRuleReference(role: string): boolean {
  return roleAtLeast(role, "operator");
}

/** #1618: the viewing business's effective rule parameter values, as `/api/config` states them. */
export interface RuleReferenceParameters {
  values: RuleParamValues | null;
  source: "code_defaults" | "tenant_set" | null;
  version: number | null;
}

export interface RuleReferenceScreenProps {
  runtimeRulesetVersion: string | null;
  /** Absent or null: the values are shown as unavailable and no threshold is printed. */
  ruleParameters?: RuleReferenceParameters | null;
  /** A tenant-less owner: there is no business whose values could be shown. */
  noBusiness?: boolean;
}

/** "20–29", or "20" for a one-number band. */
function band(from: number, to: number): string {
  return from === to ? String(from) : `${from}–${to}`;
}

/** "von 20 bis 29", or "bei 20" for a one-number band. */
function bandPhrase(from: number, to: number): string {
  return from === to ? `bei ${from}` : `von ${from} bis ${to}`;
}

/** "Gast" after the number one, else "Gäste" (dative "Gästen"). */
function guestWord(count: number, dative = false): string {
  return count === 1 ? "Gast" : dative ? "Gästen" : "Gäste";
}

/**
 * The guest bands of this business, built from its values. The ordered-threshold invariant
 * (online-shop BBQ <= written offer <= paid BBQ service) makes them contiguous; a band the values
 * leave empty is omitted rather than printed as a backwards range.
 */
function guestBandRows(values: RuleParamValues): React.ReactNode[][] {
  const bbq = values.bbqOnlineShopMinGuests;
  const offer = values.minWrittenOfferGuests;
  const paid = values.bbqPaidServiceMinGuests;
  const rows: React.ReactNode[][] = [
    ["fehlt", "Nur Gästezahl klären; kein Onlineshop-Hinweis", "Nur Gästezahl klären; keine Grill-/Wetterfragen"],
  ];
  if (bbq > 1) {
    rows.push([band(1, bbq - 1), "Onlineshop, keine weiteren Standardfragen", `Kein BBQ-Angebot; Hinweis: BBQ-Buffets ab ${bbq}`]);
  }
  if (offer > bbq) {
    rows.push([band(bbq, offer - 1), "Onlineshop, keine weiteren Standardfragen", "Onlineshop-Selbstgrillen erst nach bestätigtem geeignetem Grill vor Ort"]);
  }
  if (paid > offer) {
    rows.push([band(offer, paid - 1), "Individuelles Angebot grundsätzlich möglich", "Grill vor Ort und alle weiteren offenen Angaben werden gemeinsam geklärt; kein Grill/Koch/Grillmeister von uns"]);
  }
  rows.push([`ab ${paid}`, "Individuelles Angebot grundsätzlich möglich", "Optionaler Grill-/Koch-/Grillmeister-Service möglich, aber nie allein wegen der Gästezahl buchen"]);
  return rows;
}

/** DE/EN summary of the values, the one part of this German reference that follows the UI language. */
function EffectiveParameters({ parameters, noBusiness }: {
  parameters: RuleReferenceParameters | null;
  noBusiness: boolean;
}) {
  const t = useT();
  const { lang } = useLang();
  const values = parameters?.values ?? null;
  const shown = (key: string, value: number | boolean) =>
    typeof value === "boolean" ? t(value ? "businesses.rules.yes" : "businesses.rules.no") : key.endsWith("NetCents") ? formatRuleMoney(value, lang) : String(value);
  return (
    <div
      role="region"
      className={styles.parameters}
      lang={lang}
      aria-labelledby="rule-reference-parameters-title"
      data-testid="rule-reference-parameters"
    >
      <h2 id="rule-reference-parameters-title">
        {t(noBusiness ? "businesses.rules.title" : "ruleReference.parameters.title")}
      </h2>
      {values ? (
        <>
          <p className={styles.parametersSource}>
            {parameters?.source === "tenant_set" && parameters.version !== null
              ? t("businesses.rules.source.tenantSet", { version: parameters.version })
              : t("businesses.rules.source.defaults")}
          </p>
          <dl>
            {(Object.entries(values) as [string, number | boolean][]).map(([key, value]) => (
              <div key={key}>
                <dt>{t(`businesses.rules.param.${key}`, undefined, key)}</dt>
                <dd>{shown(key, value)}</dd>
              </div>
            ))}
          </dl>
          <p className={styles.parametersNote}>{t("ruleReference.parameters.note")}</p>
        </>
      ) : (
        <p className={styles.parametersNote}>
          {t(noBusiness ? "ruleReference.parameters.noBusiness" : "ruleReference.parameters.unavailable")}
        </p>
      )}
    </div>
  );
}

const CONTENTS = [
  ["email-facts", "E-Mail- und Faktenverarbeitung"],
  ["short-replies", "Kurze Antworten, Absagen und Budget"],
  ["rule-engine", "Aktive Rule Engine"],
  ["guest-eligibility", "Gästezahl und Angebotseignung"],
  ["required-information", "Allgemein erforderliche Angaben"],
  ["catering-questions", "Fragen nach Catering-Art"],
  ["drinks", "Getränke"],
  ["dietary", "Ernährung und Allergene"],
  ["external-lookup", "Externe Adressauflösung"],
  ["catalog-eligibility", "Katalog-Eignung"],
  ["food-selection", "Speisenauswahl"],
  ["quantities", "Mengen"],
  ["staffing", "Personal und Equipment"],
  ["offer-readiness", "Angebotsreife"],
  ["draft-send", "Entwurf, Freigabe und Versand"],
  ["limitations", "Bekannte Architektur- und Produktgrenzen"],
] as const;

const STAFFING_MATRIX = [
  [50, "1/0/4/1/2/1", "1/0/3/1/1/0", "1/0/3/1/2/1", "1/0/2/1/0/0"],
  [100, "1/1/8/2/2/1", "1/1/5/2/1/1", "1/1/5/2/2/2", "1/1/4/2/0/0"],
  [150, "1/1/10/2/2/2", "1/1/7/2/1/1", "1/1/7/2/3/2", "1/1/5/2/1/0"],
  [200, "1/1/14/3/4/2", "1/1/8/3/2/1", "1/1/8/3/4/2", "1/1/6/3/1/1"],
  [250, "1/2/16/3/4/2", "1/2/10/3/2/1", "1/2/10/3/4/2", "1/1/8/3/2/1"],
  [300, "1/2/20/4/4/4", "1/2/12/4/2/2", "1/2/12/4/6/2", "1/1/9/4/2/2"],
  [350, "1/2/24/4/4/4", "1/2/14/4/2/2", "1/2/14/4/6/2", "1/1/10/4/2/2"],
  [400, "1/2/26/5/6/4", "1/2/16/5/3/2", "1/3/16/5/8/4", "1/1/12/5/3/2"],
  [450, "1/2/26/5/6/4", "1/2/18/5/3/2", "1/3/18/5/8/4", "1/1/13/5/3/2"],
  [500, "1/2/32/6/6/6", "1/2/20/6/4/2", "1/3/20/6/8/4", "1/1/15/6/4/2"],
] as const;

function VersionState({ runtimeRulesetVersion }: RuleReferenceScreenProps) {
  const status = ruleReferenceVersionStatus(runtimeRulesetVersion);
  return (
    <div className={styles.versionBlock} data-version-status={status}>
      <dl>
        <div>
          <dt>Snapshot dieser Referenz</dt>
          <dd>{RULE_REFERENCE_SNAPSHOT_VERSION}</dd>
        </div>
        <div>
          <dt>Ruleset der laufenden Anwendung</dt>
          <dd>{runtimeRulesetVersion ?? "nicht verfügbar"}</dd>
        </div>
      </dl>
      {status === "current" ? (
        <p className={styles.versionOk} role="status">
          Referenz und laufende Anwendung verwenden dieselbe Ruleset-Version.
        </p>
      ) : (
        <p className={styles.versionWarning} role="alert">
          {status === "stale"
            ? "Achtung: Diese Referenz ist veraltet. Prüfen Sie das aktuelle Ruleset, bevor Sie Testergebnisse bewerten."
            : "Achtung: Die laufende Ruleset-Version konnte nicht geladen werden. Behandeln Sie diese Referenz bis zur Prüfung als möglicherweise veraltet."}
        </p>
      )}
    </div>
  );
}

function ReferenceTable({
  headings,
  rows,
  compact = false,
  label,
}: {
  headings: readonly string[];
  rows: readonly (readonly React.ReactNode[])[];
  compact?: boolean;
  label: string;
}) {
  return (
    <div className={styles.tableWrap}>
      <table className={`${styles.table} ${compact ? styles.compactTable : ""}`} aria-label={label}>
        <thead>
          <tr>{headings.map((heading) => <th key={heading} scope="col">{heading}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => cellIndex === 0
                ? <th key={cellIndex} scope="row">{cell}</th>
                : <td key={cellIndex}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RuleReferenceScreen({ runtimeRulesetVersion, ruleParameters, noBusiness = false }: RuleReferenceScreenProps) {
  const values = ruleParameters?.values ?? null;
  const t = useT();
  const { lang } = useLang();
  const money = (key: "weekdayFoodMinimumNetCents" | "sundayHolidayFoodMinimumNetCents" | "extrasFoodMinimumNetCents") =>
    values?.[key] === undefined ? t("ruleReference.commercial.unknown") : formatRuleMoney(values[key], lang);

  const pageRef = React.useRef<HTMLElement>(null);
  const initialAnchor = React.useRef(typeof window === "undefined" ? "" : window.location.hash.slice(1));
  const revealAnchor = React.useCallback((id: string) => {
    if (!CONTENTS.some(([sectionId]) => sectionId === id)) return;
    const section = pageRef.current?.querySelector<HTMLElement>(`section[id="${id}"]`);
    section?.scrollIntoView({ block: "start", behavior: "instant" });
    section?.querySelector<HTMLHeadingElement>("h2")?.focus({ preventScroll: true });
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    // Capture the destination before the shell stamps this history entry with its tenant scope.
    const frame = window.requestAnimationFrame(() => revealAnchor(initialAnchor.current));
    const onHashChange = (event: HashChangeEvent) => {
      revealAnchor(new URL(event.newURL).hash.slice(1));
    };
    window.addEventListener("hashchange", onHashChange);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [revealAnchor]);

  return (
    <article ref={pageRef} className={styles.page} lang="de" data-testid="rule-reference">
      <header className={styles.header}>
        <p className={styles.eyebrow}>Temporäre Testreferenz · nur lesbar</p>
        <h1>Regelreferenz für den E-Mail-bis-Angebot-Test</h1>
        <p className={styles.lead}>
          Diese Seite erklärt den aktuellen Stand für Tests. Sie ist weder fachliche noch
          technische Autorität. Maßgeblich bleiben die feste TypeScript-Rule-Engine, die
          kanonische Policy und die serverseitigen Sicherheitsprüfungen derselben Release-Revision.
        </p>
      </header>

      <VersionState runtimeRulesetVersion={runtimeRulesetVersion} />
      {ruleParameters !== undefined && <EffectiveParameters parameters={ruleParameters} noBusiness={noBusiness} />}

      <nav className={styles.contents} aria-label="Inhalt der Regelreferenz">
        <h2>16 Prüfbereiche</h2>
        <ol>
          {CONTENTS.map(([id, label]) => (
            <li key={id}>
              <a href={`#${id}`} onClick={() => {
                // Following the same anchor again does not emit hashchange.
                if (window.location.hash === `#${id}`) revealAnchor(id);
              }}>{label}</a>
            </li>
          ))}
        </ol>
      </nav>

      <div className={styles.inventory}>
        <section className={styles.section} id="email-facts">
          <h2 tabIndex={-1}>1. E-Mail- und Faktenverarbeitung</h2>
          <ul>
            <li>Betreff und vom Kunden neu verfasster Nachrichtentext werden gemeinsam ausgewertet. Zitierte Historie wird vor der Extraktion vom aktuellen Antworttext getrennt.</li>
            <li>Das Sprachmodell darf strukturierte Fakten nur mit wörtlichem Nachweis aus der Nachricht liefern. Core prüft, ob der Nachweis tatsächlich im erlaubten Quelltext vorkommt; nicht belegte Fakten werden nicht übernommen.</li>
            <li>Neuere, eindeutig belegte Kundenaussagen haben Vorrang vor älteren. Gesperrte manuelle Fakten werden nicht still überschrieben. Nicht auflösbare Konflikte bleiben sichtbar und führen zur manuellen Prüfung.</li>
            <li>Eine inhaltlich relevante Nachricht ohne belastbare extrahierte Fakten gilt als verdächtig leer: offene Entwürfe werden verworfen und der Vorgang geht in menschliche Prüfung.</li>
            <li>AI-Funktion und Monatskontingent werden tenantbezogen fail-closed geprüft. Ist Extraktion deaktiviert oder das Kontingent erschöpft, bleibt die Nachricht zur späteren Verarbeitung erhalten; sie wird nicht als erledigt verworfen.</li>
            <li>Jede echte Faktenänderung erhöht die Request-Revision und macht ältere, noch nicht gesendete Entwürfe ungültig. Wiederholungen mit identischem kanonischem Inhalt erzeugen keine künstliche Änderung.</li>
          </ul>
        </section>

        <section className={styles.section} id="short-replies">
          <h2 tabIndex={-1}>2. Kurze Antworten, Absagen und Budget</h2>
          <h3>Kurze Ja-/Nein-Antworten</h3>
          <ul>
            <li>Ein isoliertes „Ja“, „Nein“, „korrekt“ oder ähnlicher Kurztext wird nur zugeordnet, wenn es nachweisbar auf den neuesten gesendeten, freigegebenen Entwurf desselben Tenants und Vorgangs antwortet.</li>
            <li>Der Entwurf muss genau eine offene, atomare Ja-/Nein-Frage enthalten. Bei mehreren, zusammengesetzten, veralteten oder nicht eindeutig verknüpften Fragen wird nichts geraten; der Vorgang geht zur manuellen Prüfung.</li>
            <li>Die gruppierte Küchenfrage darf ein kurzes Ja/Nein nur auf „Küche vorhanden“ beziehen. Ausstattung wird dadurch nicht erfunden.</li>
          </ul>
          <h3>Absagen</h3>
          <ul>
            <li>Eine kurze, eindeutig abschließende Absage des aktuellen Termins schließt den Vorgang und wird auditiert.</li>
            <li>Fragen, Möglichkeiten, historische Absagen, Verneinungen der Absage oder eine Absage zusammen mit einer neuen substanziellen Anfrage sind nicht eindeutig und gehen zur manuellen Prüfung.</li>
          </ul>
          <h3>Budget</h3>
          <ul>
            <li>{values === null
              ? <>Ob das Gesamtbudget Pflichtangabe ist, legt <code>budgetMandatory</code> fest; der Wert dieses Betriebs ist nicht verfügbar.</>
              : values.budgetMandatory
                ? <>Das Gesamtbudget ist für diesen Betrieb Pflichtangabe: <code>budgetMandatory=true</code>. Ein fehlendes Budget erzeugt eine Rückfrage.</>
                : <>Das Gesamtbudget ist für diesen Betrieb optional: <code>budgetMandatory=false</code>. Ein fehlendes Budget erzeugt keine Rückfrage.</>}</li>
            <li>Wenn der Kunde ein Budget nennt, zählt nur ein einzelner positiver Gesamtbetrag in EUR mit wörtlichem Nachweis. Pro-Kopf-Beträge, Bereiche, mehrere Beträge, andere Währungen oder mehrdeutige Trennzeichen bleiben unaufgelöst.</li>
            <li>Ein autoritatives Gesamtbudget begrenzt den gesamten vorgeschlagenen Nettowert. Ein Vorschlag oberhalb des Budgets wird nicht erzeugt.</li>
          </ul>
        </section>

        <section className={styles.section} id="rule-engine">
          <h2 tabIndex={-1}>3. Aktive Rule Engine</h2>
          <ul>
            <li>Aktiv sind 38 versionierte Regeln aus dem statischen TypeScript-Registry. Regeln werden nach Priorität ausgewertet; harte Business-Limits überstimmen normale Checklisten.</li>
            <li>{values === null
              ? <>Die Werte der fünf registrierten Parameter dieses Betriebs sind nicht verfügbar.</>
              : <>Parameter dieses Betriebs: BBQ-Onlineshop ab {values.bbqOnlineShopMinGuests} {guestWord(values.bbqOnlineShopMinGuests, true)}, individuelles schriftliches Angebot ab {values.minWrittenOfferGuests}, optionaler bezahlter BBQ-Service ab {values.bbqPaidServiceMinGuests}, <code>budgetMandatory={String(values.budgetMandatory)}</code> und <code>fieldGroupsCombined={String(values.fieldGroupsCombined)}</code>.</>}{" "}
              Regeln, Grenzen und Abhängigkeiten stehen im Code. {t("ruleReference.parameters.note")}</li>
            <li>Frühere Datenbankzeilen zu RuleSpec, RuleParams oder Policies sind nur unveränderliche Audit-/Replay-Historie. Sie wählen das aktuelle Laufzeitverhalten nicht aus.</li>
            <li>Es gibt keine Rule-Studio-Bearbeitung, keinen tenantabhängigen Regelsatz und keine AI-Autorenschaft für Business-Entscheidungen. Fehler oder nicht beweisbare Zustände werden nicht durch einen permissiven Fallback ersetzt.</li>
            <li>Historische Entscheidungen behalten ihre damalige Ruleset-Identität und werden mit den dafür eingefrorenen Regelkörpern reproduziert; neue Entscheidungen verwenden den aktuellen Code-Regelsatz.</li>
          </ul>
        </section>

        <section className={styles.section} id="guest-eligibility">
          <h2 tabIndex={-1}>4. Gästezahl und Angebotseignung</h2>
          {values === null ? (
            <p className={styles.note}>Die Gästegrenzen dieses Betriebs sind nicht verfügbar.</p>
          ) : (
            <ReferenceTable
              label="Gästegrenzen für Angebote"
              headings={["Gästeangabe", "Nicht-BBQ", "BBQ"]}
              rows={guestBandRows(values)}
            />
          )}
          <ul>
            <li>Ein sauberer geschlossener Bereich aus zwei positiven ganzen Zahlen verwendet intern den arithmetischen Mittelwert, bei .5 aufgerundet. Der Originalbereich bleibt als Kundennachweis erhalten.</li>
            <li>„unter 40“, „bis 30“, „50+“, „mindestens 50“, offene oder fehlerhafte Bereiche bleiben unaufgelöst. Im Hamburg-Kommerzpfad führen solche Angaben zur Beratung; sonst wird die konkrete Gästezahl geklärt.</li>
            <li>Eine ungefähre Einzelzahl kann die normale Angebotsschwelle unterstützen, schaltet aber den bezahlten BBQ-Service {values === null ? <>ab <code>bbqPaidServiceMinGuests</code></> : `ab ${values.bbqPaidServiceMinGuests}`} nicht frei. Dafür ist eine bestätigte bzw. sauber berechenbare wirksame Zahl erforderlich.</li>
            <li>Null oder negative Gästezahlen sind ungültig und erzwingen menschliche Prüfung.</li>
            <li>Im BBQ-Selbstgrillband (ab <code>bbqOnlineShopMinGuests</code> bis unter <code>minWrittenOfferGuests</code>{values !== null && values.minWrittenOfferGuests > values.bbqOnlineShopMinGuests ? `, hier ${bandPhrase(values.bbqOnlineShopMinGuests, values.minWrittenOfferGuests - 1)}` : ""}) wird bei einer konkreten, nicht ungefähren Einzelzahl zuerst nur nach einem geeigneten Grill vor Ort gefragt. Im Angebotsband unter dem bezahlten BBQ-Service (ab <code>minWrittenOfferGuests</code> bis unter <code>bbqPaidServiceMinGuests</code>{values !== null && values.bbqPaidServiceMinGuests > values.minWrittenOfferGuests ? `, hier ${bandPhrase(values.minWrittenOfferGuests, values.bbqPaidServiceMinGuests - 1)}` : ""}) kommt diese Frage zusammen mit allen anderen anwendbaren offenen Angaben in dieselbe erste Rückfrage. „Nein“ blockiert in beiden Bereichen sowohl Angebot als auch Onlineshop-Redirect.</li>
          </ul>
        </section>

        <section className={styles.section} id="required-information">
          <h2 tabIndex={-1}>5. Allgemein erforderliche Angaben</h2>
          <p>Nach bekannter angebotstauglicher Gästezahl werden alle noch offenen Basispunkte in einer Antwort gesammelt:</p>
          <ul>
            <li>Veranstaltungsdatum und Beginn; Enddatum nur bei einem Hinweis auf Mehrtägigkeit.</li>
            <li>Telefonnummer und vollständige Veranstaltungsadresse mit Straße, Hausnummer, PLZ und Ort.</li>
            <li>Getränke von uns, Servicepersonal, Geschirr/Besteck und Buffettische — nur soweit das gespeicherte Extras-Gate diese von uns angebotenen Zusatzleistungen zulässt.</li>
            <li>Lieferzugang als bedingte Gruppenfrage: ebenerdig oder, falls nicht, Aufzug vorhanden und für die Liefermenge geeignet. Bekannte Teilfakten werden nicht wiederholt; ein mehrdeutiges Ja/Nein wird nicht geraten.</li>
            <li>Vegetarische/vegane Wünsche, Allergien und Unverträglichkeiten sowie die Catering-Art, wenn sie noch unklar ist.</li>
          </ul>
          <p className={styles.note}>Nie standardmäßig fragen: Kunden-E-Mail oder eine abweichende Lieferadresse. Eine einzelne genannte Adresse gilt für Rechnung und Lieferung; zwei Adressen werden nur bei eindeutiger Kundenkennzeichnung getrennt.</p>
        </section>

        <section className={styles.section} id="catering-questions">
          <h2 tabIndex={-1}>6. Fragen nach Catering-Art</h2>
          <ul>
            <li>Erkannte Typen sind Fingerfood, Menü, Buffet, BBQ/Grillen, Messe/Standcatering und Live-Cooking. Mehrere klar genannte Typen können gleichzeitig aktiv sein; überlappende Fragen werden dedupliziert.</li>
            <li>Ist kein Typ klar, wird nach bekannter angebotstauglicher Gästezahl in derselben Runde wie die Basispunkte nach der Catering-Richtung gefragt. Typ-spezifische Fragen warten bis zur Klärung.</li>
            <li><strong>Fingerfood:</strong> Sattwerden oder Snack/Imbiss, nur herzhaft oder auch süß/Dessert, Flying oder Buffetaufbau. „Fingerfood als Buffet“ bleibt Fingerfood.</li>
            <li><strong>Menü/Buffet:</strong> Gangzahl, Küche/Pantry und Geräte, Bestuhlung, kulinarische Richtung. „BBQ-Buffet“ und „Grillbuffet“ bleiben BBQ und aktivieren nicht zusätzlich die generische Buffet-Checkliste.</li>
            <li><strong>BBQ:</strong> je nach Gästezahl geeigneter Grill vor Ort, optionaler Grill/Koch/Grillmeister und Wetterschutz/Pavillon. Im Angebotsband unter dem bezahlten BBQ-Service{values !== null && values.bbqPaidServiceMinGuests > values.minWrittenOfferGuests ? ` (${bandPhrase(values.minWrittenOfferGuests, values.bbqPaidServiceMinGuests - 1)} ${guestWord(values.bbqPaidServiceMinGuests - 1, true)})` : ""} werden Grill vor Ort, Wetterschutz/Pavillon und die übrigen offenen Angaben in derselben ersten Rückfrage gesammelt. Wetterschutz wird ab angebotstauglicher wirksamer Zahl gefragt, außer das Event ist ausdrücklich indoor; bei angefragtem Grill-Setup auch indoor.</li>
            <li><strong>Messe:</strong> Speisenrichtung, Halle, Standnummer, Etagen, Küche/Pantry samt Ausstattung, Stand-Equipment, Kaffee-Setup und Mehrweg-/Einweggeschirr. Das Enddatum kommt nur bei Mehrtägigkeit hinzu.</li>
            <li><strong>Live-Cooking:</strong> wird als Typ erkannt, besitzt aber derzeit keine eigene automatische Speisen-Checkliste oder automatische Speisenpositionen.</li>
          </ul>
        </section>

        <section className={styles.section} id="drinks">
          <h2 tabIndex={-1}>7. Getränke</h2>
          <ul>
            <li>Wenn Getränke von uns gewünscht sind, aber keine Sorte genannt ist, erlaubt die Rule Engine das Standard-Getränkepaket für die gesamte Gästezahl; keine Einzelflaschen.</li>
            <li>Konkrete Getränkewünsche von uns haben Vorrang vor dem Standardpaket.</li>
            <li>Getränke durch Location/Kunde, bereits vor Ort, „keine Getränke“, „ohne Getränke“ oder „nicht benötigt“ bedeuten: kein Paket und keine erneute Frage.</li>
            <li>{t("ruleReference.commercial.extras", { amount: money("extrasFoodMinimumNetCents") })}</li>
            <li>Produktgrenze: Im aktuellen Katalog ist kein eindeutig bestimmtes Standard-Getränkepaket hinterlegt. Deshalb erzeugt der Angebotsvorschlag trotz erlaubter Regel keine automatische Getränkeposition und ersetzt sie nicht durch Flaschen.</li>
          </ul>
        </section>

        <section className={styles.section} id="dietary">
          <h2 tabIndex={-1}>8. Ernährung und Allergene</h2>
          <ul>
            <li>Ein strukturiertes Profil trennt vegane, vegetarische und uneingeschränkte Gäste mit expliziter Überlappungslogik. Die Summe muss zur maßgeblichen Gästezahl passen.</li>
            <li>Unvollständigkeit — etwa Einschränkung ohne Anzahl oder unklare vegan/vegetarisch-Überlappung — erzeugt eine gezielte Rückfrage. Widersprüche, ungültige Profile oder Kohorten über der Gesamtzahl gehen in manuelle Prüfung.</li>
            <li>Vegane Gäste benötigen ein nachweisbar veganes Paket. Vegetarische Gäste bevorzugen ein vegetarisches und dürfen auf ein veganes Paket ausweichen. Uneingeschränkte Gäste benötigen ein Standardpaket.</li>
            <li>Alle erforderlichen Kohorten müssen vollständig abgedeckt werden. Fehlt ein kompatibles Paket, wird kein partiell „sicherer“ Speisenvorschlag erzeugt.</li>
            <li>Halal, Allergien, Unverträglichkeiten oder andere Anforderungen, deren Sicherheit der Katalog nicht beweist, blockieren automatische Speisenpositionen bis zur manuellen Artikelprüfung.</li>
            <li>Nur der historische skalare Pfad darf bei erwähntem vegetarisch/vegan ohne exakte Anzahl 20 % Veggie und 80 % Standard berechnen. Das strukturierte Profil erfindet niemals einen 20/80-Split.</li>
          </ul>
        </section>

        <section className={styles.section} id="external-lookup">
          <h2 tabIndex={-1}>9. Externe Adressauflösung</h2>
          <ul>
            <li>Ein bloßer Venue- oder Messename ist keine vollständige Adresse. Ohne gespeicherte Straße, Hausnummer, PLZ und Ort bleibt die Adressfrage offen.</li>
            <li>Eine Auflösung läuft nur bei aktivem globalem Kill-Switch, aktivem Tenant, aktivierter Tenant-Funktion und verfügbarem Kontingent. Standardmäßig ist <code>EXTERNAL_LOOKUP_ENABLED=false</code>.</li>
            <li>Die deterministische bekannte-Venues-Liste wird vor Google Places geprüft. Google wird ohne Credential oder Quota-Reservierung nicht aufgerufen. Generische Ortswörter aktivieren keinen Lookup.</li>
            <li>Eine Kunden- oder manuell bestätigte Adresse wird nie überschrieben. Ergebnisse werden als <code>externally_resolved</code> oder — aus der bekannten-Venues-Liste — als <code>curated_venue_resolved</code> gespeichert und machen ältere offene Entwürfe ungültig.</li>
            <li>Eine über Google Places ermittelte Adresse erzwingt Operatorprüfung und muss bestätigt oder korrigiert werden. Eine Adresse aus der bekannten-Venues-Liste gilt ohne Bestätigung; ein Operator kann sie weiterhin korrigieren. Ein extern ermitteltes Messedatum wird dem Kunden zur Bestätigung vorgelegt. Spätere Kundenaussagen haben Vorrang.</li>
          </ul>
        </section>

        <section className={styles.section} id="catalog-eligibility">
          <h2 tabIndex={-1}>10. Katalog-Eignung</h2>
          <p>Eine Speisenposition darf nur in den Vorschlag, wenn alle folgenden Nachweise gleichzeitig erfüllt sind:</p>
          <ul>
            <li>Artikel stammt aus dem Katalog des aktuellen Tenants, ist aktiv und gehört exakt zur Catering-Familie.</li>
            <li>Name oder Beschreibung tragen keinen konkreten historischen Datumsstempel. Bei der anlassbasierten Auswahl benannter Gruppenartikel werden außerdem Reinigungs-, Pauschal-, Miet-, Transport-, Liefer-, Test-, Muster- und Dummy-Artikel ausgeschlossen.</li>
            <li>Gästezahl liegt innerhalb der Artikelgrenzen und der Nettopreis ist vorhanden, endlich und positiv.</li>
            <li>Ernährungsprofil und Kandidaten-Metadaten beweisen die benötigte Verträglichkeit.</li>
            <li>Ein autoritatives Gesamtbudget wird nicht überschritten.</li>
          </ul>
          <p>Fehlende aktuelle Paket-, Preis- oder Anlassdaten führen zu <code>DEFER</code>. Ungültige, widersprüchliche oder sicherheitskritische Evidenz führt zu <code>CONSULTATION</code>. In beiden Fällen wird kein Teilvorschlag erfunden.</p>
        </section>

        <section className={styles.section} id="food-selection">
          <h2 tabIndex={-1}>11. Speisenauswahl</h2>
          <ul>
            <li>Nur deterministischer TypeScript-Code wählt BP-ID, Artikel und Menge. Das Sprachmodell sieht keinen Katalog und trifft keine Auswahlentscheidung.</li>
            <li>Ohne vom Operator gewählten Anlass bleibt der Paketpfad: Fingerfood wählt ein passendes Flying-Fingerfood-Paket; Buffet und eingängiges Menü buchen ein passendes Paket pro Person.</li>
            <li>Bei Fingerfood kann ein Anlass eine Komposition aus benannten Gruppenartikeln aktivieren. Fehlen genug geeignete Artikel, fällt der Vorschlag sichtbar auf ein vollständiges Paket zurück.</li>
            <li>Bei Menü/Buffet aktiviert ein Anlass die Flying-Komposition oder bei exakt zwei, drei oder vier Gängen einzelne Gruppenartikel. Paket und Einzelgänge werden nie gleichzeitig gebucht.</li>
            <li>Live-Cooking- und Messe-Speisenpositionen sind derzeit manuell. Es wird kein automatisches Food-Paket vorgetäuscht.</li>
            <li>Bei mehreren Catering-Typen gilt für den automatischen Hauptvorschlag die feste Reihenfolge BBQ, Menü, Buffet, Fingerfood, Live-Cooking. Weitere klare Typen können dennoch eigene Fragen aktivieren.</li>
          </ul>
        </section>

        <section className={styles.section} id="quantities">
          <h2 tabIndex={-1}>12. Mengen</h2>
          <p>Mengen verwenden die konkrete Gästezahl oder den zulässigen Mittelwert eines sauberen geschlossenen Bereichs. Abweichende, klar genannte Kundenzahlen haben Vorrang.</p>
          <ReferenceTable
            label="Stückmengen nach Anlass"
            headings={["Pfad", "Anlass", "Default pro Person", "Zusammensetzung"]}
            rows={[
              ["Fingerfood", "kurzer_empfang", "4 Teile", "4 herzhaft; bei Süßwunsch 4 herzhafte + 2 süße Varianten"],
              ["Fingerfood", "empfang_premium", "5 Teile", "wie oben, Gesamtmenge bleibt 5"],
              ["Fingerfood", "lunch", "9 Teile", "wie oben, Gesamtmenge bleibt 9"],
              ["Fingerfood", "flying_dinner", "11 Teile", "wie oben, Gesamtmenge bleibt 11"],
              ["Flying Buffet", "snack", "4 Teile", "4 kalte Varianten"],
              ["Flying Buffet", "lunch", "7 Teile", "1 warm, 5 kalt, 1 Dessert"],
              ["Flying Buffet", "dinner", "9 Teile", "1 warm, 7 kalt, 1 Dessert"],
              ["Flying Buffet", "premium", "11 Teile", "2 warm, 7 kalt, 2 Dessert"],
            ]}
          />
          <ul>
            <li>Menü: zwei Gänge = Hauptgericht + Dessert; drei = Suppe + Hauptgericht + Dessert; vier = kalte Vorspeise + Suppe + Hauptgericht + Dessert. Jede Position wird pro Gast gebucht. Andere Gangzahlen bleiben manuell.</li>
            <li>Paketpfad: ein Menü-/Buffet-/Fingerfood-Paket pro Gast. Ein theoretisches Standard-Getränkepaket wäre ebenfalls einmal pro Gast, wird aktuell mangels bestimmtem Katalogartikel nicht gebucht.</li>
            <li>Fachliche Defaults: Blattsalat 90 g, Feinkostsalat 175 g, Vorspeisensuppe 250 ml, Hauptsuppe 450 ml, warme Speisen 400 g, Protein 200 g, Beilage 150 g, Gemüse 110 g, Dessert 1 Glas, Obst 165 g, Kuchen 2 Stück, BBQ 500 g pro Person.</li>
            <li>Aktuell ist nur ein Liter-Artikel der Gruppe Suppe als 250-ml-Vorspeise sicher umrechenbar. „Platte“ wird nur mit expliziter Kapazität „für N Pers.“ umgerechnet. Kg- und sonstige physische Artikel bleiben ohne beweisbare Packungsgröße blockiert.</li>
          </ul>
        </section>

        <section className={styles.section} id="staffing">
          <h2 tabIndex={-1}>13. Personal und Equipment</h2>
          <ul>
            <li>Policy-Version <code>2026-07-08.1</code>. Die Funktion ist code-seitig aktiv, aber der Operator muss Personal/Equipment ausdrücklich in den Vorschlag aufnehmen. Nicht-BBQ benötigt zusätzlich ein Planungsformat; jeder Pfad benötigt eine Einsatzdauer. BBQ benötigt dagegen kein Planungsformat.</li>
            <li>{t("ruleReference.commercial.extras", { amount: money("extrasFoodMinimumNetCents") })}</li>
            <li>Zulässige Dauer: größer 0 bis einschließlich 72 Stunden. Personalmenge = Kopfzahl × Dauer; der Katalogartikel muss eine Stunden-Einheit tragen. Equipment bleibt pauschal.</li>
            <li>Zwischen den 50er-Ankern werden Küchenrollen aufgerundet interpoliert; Service folgt der festen Formatquote. Unter 50 und über 500 wird anhand derselben Regeln skaliert. Fine Dining erhält nur einen manuellen Prüfhinweis von +10–20 %, keine automatische Erhöhung.</li>
          </ul>
          <p className={styles.note}>Matrix-Reihenfolge je Zelle: Veranstaltungsleitung / Serviceleitung / Service / Bar / Köche / Küchenhilfe.</p>
          <ReferenceTable
            compact
            label="Personal-Matrix nach Gästezahl und Format"
            headings={["Gäste", "Set-Menü", "Flying", "Live-Cooking/Flying", "Buffet"]}
            rows={STAFFING_MATRIX}
          />
          <ul>
            <li>Bar: bis 75 Gäste 1, bis 150 = 2, bis 250 = 3, bis 350 = 4, bis 450 = 5, darüber 6; Cocktailbar konservativ 1 pro 45 Gäste.</li>
            <li>Die BBQ-Servicegrenze ist kein fester Wert dieser Personal-Policy. Sie folgt dem am Vorgang gepinnten <code>bbqPaidServiceMinGuests</code>. {values === null ? <>Ohne gültige Parameterwerte bleibt die konkrete Grenze nicht bestimmbar.</> : <>Für neue Vorgänge dieses Betriebs bleibt BBQ unter {values.bbqPaidServiceMinGuests} Gästen Selbstgrillen; ab {values.bbqPaidServiceMinGuests} kann bei bestätigter, nicht ungefährer Gästezahl und aktivem Operator-Personalblock ein Grillmeister stundenbasiert und ein Gasgrill pauschal vorgeschlagen werden.</>} Bereits offene Vorgänge behalten ihren gepinnten Wert; die Rule Engine bucht diese Leistungen nie allein aus der Gästezahl.</li>
            <li>Fest in dieser Personal-Policy bleiben die übrigen Schwellen: Live-Cooking-Kochstationen sind unter 50 Gästen gesperrt; DJ und Blumendekoration werden ab 80 nur als manueller Prüfhinweis genannt. Fehlt die exakte Rollen-Zuordnung oder Einheit im Katalog, wird der gesamte Personal-/Equipment-Block ausgelassen statt teilweise gebaut.</li>
          </ul>
        </section>

        <section className={styles.section} id="offer-readiness">
          <h2 tabIndex={-1}>14. Angebotsreife</h2>
          <ReferenceTable
            label="Hamburger kommerzielle Mindestwerte"
            headings={["Fall", "Speisen netto", "Ergebnis"]}
            rows={[
              ...(values === null
                ? [[<>unter <code>minWrittenOfferGuests</code> Gästen</>, "nicht bewertet", "ONLINE_SHOP_ENTRY"]]
                : values.minWrittenOfferGuests > 1
                  ? [[`${band(1, values.minWrittenOfferGuests - 1)} ${guestWord(values.minWrittenOfferGuests - 1)}`, "nicht bewertet", "ONLINE_SHOP_ENTRY"]]
                  : []),
              [t("ruleReference.commercial.weekday"), t("ruleReference.commercial.below", { amount: money("weekdayFoodMinimumNetCents") }), "INDIVIDUAL_OFFER_PICKUP_ONLY; Lieferung verboten"],
              [t("ruleReference.commercial.weekday"), t("ruleReference.commercial.from", { amount: money("weekdayFoodMinimumNetCents") }), "INDIVIDUAL_OFFER"],
              [t("ruleReference.commercial.sunday"), t("ruleReference.commercial.below", { amount: money("sundayHolidayFoodMinimumNetCents") }), "CONSULTATION"],
              [t("ruleReference.commercial.sunday"), t("ruleReference.commercial.from", { amount: money("sundayHolidayFoodMinimumNetCents") }), "INDIVIDUAL_OFFER"],
              ["Mehrtagesevent vollständig", "nicht automatisch bewertet", "CONSULTATION"],
            ]}
          />
          <ul>
            <li>Ab {values === null
              ? <><code>minWrittenOfferGuests</code> Gästen</>
              : `${values.minWrittenOfferGuests} ${guestWord(values.minWrittenOfferGuests, true)}`} muss derselbe Request-Stand einen autoritativen Speisen-Nettowarenwert liefern. Er enthält nur Speisen nach positionsbezogenen Rabatten, nicht Getränke, Personal, Equipment, Logistik, Steuer oder globale unklare Rabatte.</li>
            <li>Fehlende Kundeneingaben oder unverfügbare aktuelle Speisen-/Preisbelege ergeben <code>DEFER</code>. Ungültige Fakten, Datum, Katalogevidenz oder ein Kalenderdatum außerhalb 2026–2030 ergeben <code>CONSULTATION</code>.</li>
            <li>Gültige Datumsformen sind YYYY-MM-DD, D.M.YYYY und D. deutscher Monatsname YYYY. Bereiche, mehrere Daten, fehlendes Jahr oder nicht deutsche Monatsnamen werden nicht geraten.</li>
            <li>Erstanfragen müssen bis 14:00 Uhr Europe/Berlin am vorherigen Hamburger Arbeitstag vollständig sein; für Samstag/Sonntag gilt Donnerstag 14:00, bei Feiertag weiter rückwärts. Gleichheit ist noch fristgerecht.</li>
            <li>Materielle Änderungen an individuellen Angeboten sind bis zum vollständigen fünften Hamburger Arbeitstag vor dem Event erlaubt; ab Mitternacht des Folgetags sind sie spät und ersetzen wirksame Fakten nicht.</li>
            <li>Eine request-verknüpfte aufgegebene Onlineshop-Bestellung ist unveränderlich. Echte spätere Deltas werden als <code>CHANGE_FORBIDDEN</code> gespeichert; kanonisch identische Wiederholungen ändern nichts.</li>
            <li>Der Angebots-Read-Pfad schreibt keine Positionen, Entwürfe oder Exporte. Er zeigt nur einen überprüfbaren Vorschlag aus dem aktuellen tenant-eigenen Katalog.</li>
          </ul>
        </section>

        <section className={styles.section} id="draft-send">
          <h2 tabIndex={-1}>15. Entwurf, Freigabe und Versand</h2>
          <ul>
            <li>Core baut einen schema-validierten <code>ResponsePlan</code> mit Fakten, Pflichtfragen, festen Hinweisen und erlaubten/verbotenen Themen. Das Sprachmodell darf nur die nicht autoritative deutsche Prosa formulieren und nichts hinzufügen.</li>
            <li>Normale Entscheidungen mit <code>handover_human</code> werden nicht automatisch komponiert. Ausnahme: fest determinierte Hamburg-Cutoff-Texte können als Entwurf entstehen, bleiben aber vollständig prüf- und freigabepflichtig.</li>
            <li>Jeder neue Entwurf ersetzt ältere offene Entwürfe. Bearbeitung setzt den Status auf „edited“ und löscht eine frühere Freigabe. Freigegeben werden darf nur der aktuelle, nicht ersetzte Entwurf der aktuellen Request-Revision und neuesten Entscheidung.</li>
            <li>Vor Freigabe und nochmals vor Versand werden Anhänge, Version, Revision, Entscheidung, Tenant, Zuweisung und eine inzwischen eingetroffene Kundenantwort geprüft.</li>
            <li>Für normale Kunden-E-Mail gibt es keinen Auto-Versand. Versand benötigt eine menschliche Freigabe sowie gleichzeitig aktiven Env-Kill-Switch, Admin-Schalter, Tenant-Schalter und aktiven Tenant.</li>
            <li>Ein enger, inhabergeführter QA-Konversationsmodus darf ohne Person freigeben und senden — ausschließlich für einen vom Server selbst injizierten und nachweisbaren Testlauf an die dafür vom Server erzeugte Antwortadresse. <code>TEST_MAIL_AUTOMATION_ENABLED</code>, <code>TEST_MAIL_REPLY_DOMAIN</code> und die Tenant-Funktion <code>test_injection</code> müssen aktiv sein; die Einstellungen fehlen bzw. sind standardmäßig aus. Der Pfad delegiert an dieselben Review-/Send-Services und kann deren Sperren nur weiter einschränken.</li>
            <li>Gesendet wird ausschließlich über die SMTP-Identität des betroffenen Tenants und an das aus der Inbound-Konversation verifizierte Ziel. Derselbe Idempotency-Key verhindert parallele Duplikate.</li>
            <li><code>UNKNOWN</code> bedeutet: Der SMTP-Server könnte die Nachricht angenommen haben. Dieser Zustand wird nie automatisch erneut gesendet; ein Operator muss ihn abgleichen.</li>
          </ul>
        </section>

        <section className={styles.section} id="limitations">
          <h2 tabIndex={-1}>16. Bekannte Architektur- und Produktgrenzen</h2>
          <ul>
            <li>Diese Seite ist ein manuell gepflegter Snapshot und keine Runtime-Autorität. Eine Versionsabweichung oben macht sie sichtbar veraltet; sie aktualisiert sich nicht selbst aus Policy-Dateien.</li>
            <li>Die früheren Routen <code>/rules</code> und <code>/boundary</code> bleiben absichtlich deaktiviert und fallen auf den Posteingang zurück. Regellogik und Kundentexte bleiben im Code festgelegt. Admins können unter Einstellungen → Regelwerte die für ihren Betrieb freigegebenen Werte innerhalb der Plattformgrenzen ändern, wenn die Funktion aktiviert ist. Nicht freigegebene Werte bleiben Plattformaufgabe.</li>
            <li>Externe Adressauflösung ist standardmäßig aus und hängt zusätzlich von Tenant-Funktion, Quoten und Provider-Konfiguration ab. Ohne Ergebnis wird die Adresse vom Kunden erfragt.</li>
            <li>Standard-Getränkepaket, Messe-Speisen und Live-Cooking-Speisen besitzen derzeit keinen vollständigen automatischen Katalogpfad. Sie bleiben sichtbar zur manuellen Anreicherung statt durch Ersatzartikel geschätzt zu werden.</li>
            <li>Bekannte Kataloglücke: Der Paketpfad filtert Test-/Muster-/Dummy-Namen derzeit nicht eigens. Solche aktiven, sonst geeigneten Pakete müssen im Tenant-Katalog ausgeschlossen oder bei der Prüfung erkannt werden; die strengere Namenssperre gilt nur für benannte Gruppenartikel.</li>
            <li>Anlassbasierte Gruppenartikel-Komposition kann exakte gemischte Ernährungskohorten noch nicht sicher erhalten; dann wird auf den vollständigen Paketpfad zurückgefallen.</li>
            <li>Die BankettProfi-Ausgabe ist nur eine in-memory Dry-Run-Vorschau. <code>BP_EXPORT_ENABLED</code> bleibt fest aus, und <code>commitExport()</code> verweigert immer — auch für Tests.</li>
            <li>Die Referenz verändert keine Regeln, Requests, Entwürfe, Flags, Katalogdaten oder Laufzeitkonfiguration und fügt keinen Backend-Endpunkt hinzu.</li>
          </ul>
        </section>
      </div>
    </article>
  );
}
